import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGet } from '../lib/api';
import type { CertData } from '../components/CertCard';
import { LoadingDots } from '../components/LoadingDots';

interface Device { id: number; deviceId: number; name: string | null }

/** Route: `/dashboard`. Overview with cert/device counts, active certs, and quick links. */
export function DashboardHome() {
  const { user } = useAuth();
  const [keyCount, setKeyCount] = useState<number | null>(null);
  const [certs, setCerts] = useState<CertData[] | null>(null);
  const [devices, setDevices] = useState<Device[] | null>(null);

  useEffect(() => {
    apiGet<CertData[]>('/v1/certs').then(setCerts).catch(() => setCerts([]));
    apiGet<{ devices: Device[] }>('/v1/devices').then((r) => setDevices(r.devices)).catch(() => setDevices([]));
    apiGet<{ keys: Array<{ status: string }> }>('/v1/keys')
      .then((r) => setKeyCount(r.keys.filter((k) => k.status === 'active' || k.status === 'expiring').length))
      .catch(() => setKeyCount(0));
  }, []);

  const now = Math.floor(Date.now() / 1000);
  const active = certs?.filter((c) => c.status === 'active' && (!c.expiresAt || c.expiresAt > now)) ?? [];
  const expired = certs?.filter((c) => c.expiresAt !== null && c.expiresAt <= now) ?? [];

  return (
    <div>
      <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 500 }}>welcome, {user?.orgName ?? '—'}</h2>
      <hr className="divider" />

      {keyCount === 0 && (
        <div className="feedback warn" style={{ marginBottom: 16 }}>
          ⚠ no active api keys yet.{' '}
          <Link to="/dashboard/keys" style={{ color: 'var(--color-accent)' }}>
            mint your first key →
          </Link>
        </div>
      )}

      <div className="grid-2" style={{ marginBottom: 24 }}>
        <div>
          <span className="label">certs</span>
          <div>
            {certs === null ? <LoadingDots /> : `${active.length} active · ${expired.length} expired`}
          </div>
        </div>
        <div>
          <span className="label">devices</span>
          <div>{devices === null ? <LoadingDots /> : `${devices.length} registered`}</div>
        </div>
        <div>
          <span className="label">api keys</span>
          <div>{keyCount === null ? <LoadingDots /> : `${keyCount} active`}</div>
        </div>
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── active certifications ─────────────────────</div>
      {certs === null ? (
        <LoadingDots />
      ) : active.length === 0 ? (
        <p className="text-muted">
          no active certifications. <Link to="/dashboard/certs">→ request a test cert</Link>
        </p>
      ) : (
        <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
          {active.map((c) => (
            <li key={c.id}>
              · {c.schema} TIER {c.tier} — issued {new Date(c.issuedAt * 1000).toISOString().slice(0, 10)}
            </li>
          ))}
        </ul>
      )}

      <hr className="divider" />
      <div className="text-muted" style={{ marginBottom: 8 }}>── quick access ──────────────────────────────</div>
      <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
        <li><Link to="/dashboard/console">→ test a payload</Link></li>
        <li><Link to="/dashboard/certs">→ manage certifications</Link></li>
        <li><Link to="/dashboard/devices">→ register a device</Link></li>
      </ul>
    </div>
  );
}
